import { createUseStyles } from 'react-jss';

import type { UiBaseProps } from './types';

/**
 * Text that is present for assistive technology but not painted.
 *
 * The case this exists for is a graphic standing in for words — the brand logo
 * used as the page heading. An `<svg>` contributes no text, so a heading whose
 * only child is a logo is an empty heading: it announces as blank, it is
 * invisible to anything reading the document outline, and it does not match a
 * text query. Putting the words back inside the heading fixes all three at
 * once, and is why this is a text alternative rather than an `aria-label` on
 * the graphic. (`aria-label` on a bare `<svg>` is unreliable anyway — without
 * `role="img"` many screen readers ignore the element entirely.)
 *
 * The clip-rect technique below is deliberate. `display: none`,
 * `visibility: hidden` and `width: 0` all remove the text from the
 * accessibility tree as well as from the screen, which defeats the point.
 */
const useStyles = createUseStyles({
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    // A 1px box would still be a visible dot at some zoom levels; clipping the
    // painted area to nothing avoids that without shrinking the box to zero,
    // which some engines treat as "not rendered".
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    border: 0,
    padding: 0,
    margin: -1,
  },
});

export default function VisuallyHidden({ children, testId, className, style }: UiBaseProps) {
  const styles = useStyles();

  return (
    <span
      className={className ? `${styles.visuallyHidden} ${className}` : styles.visuallyHidden}
      style={style}
      data-testid={testId}
    >
      {children}
    </span>
  );
}
